import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { baseApiURL } from '../../baseUrl';
import { FiFileText, FiDownload, FiBarChart2, FiUsers, FiTrendingUp, FiRefreshCw } from 'react-icons/fi';

const Reports = () => {
    const [drives, setDrives] = useState([]);
    const [allApplications, setAllApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [driveStats, setDriveStats] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [compRes, driveRes] = await Promise.all([
                axios.get(`${baseApiURL()}/placement/companies/get`),
                axios.get(`${baseApiURL()}/placement/drives/get`),
            ]);
            const drivesData = driveRes.data.drives || [];
            setDrives(drivesData);

            // Fetch applications for each drive
            const appPromises = drivesData.map(d =>
                axios.get(`${baseApiURL()}/placement/applications/drive/${d._id}`)
                    .then(res => ({ driveId: d._id, drive: d, applications: res.data.applications || [] }))
                    .catch(() => ({ driveId: d._id, drive: d, applications: [] }))
            );
            const results = await Promise.all(appPromises);

            // Combine all applications
            const allApps = [];
            const stats = results.map(r => {
                const apps = r.applications;
                apps.forEach(a => allApps.push({ ...a, driveName: r.drive.title, companyName: r.drive.company?.name || '' }));
                return {
                    driveId: r.driveId,
                    driveTitle: r.drive.title,
                    companyName: r.drive.company?.name || 'N/A',
                    driveDate: r.drive.driveDate,
                    status: r.drive.status,
                    total: apps.length,
                    applied: apps.filter(a => a.status === 'Applied').length,
                    shortlisted: apps.filter(a => a.status === 'Shortlisted').length,
                    selected: apps.filter(a => a.status === 'Selected').length,
                    rejected: apps.filter(a => a.status === 'Rejected').length,
                    waitlisted: apps.filter(a => a.status === 'Waitlisted').length,
                    selectionRate: apps.length > 0
                        ? ((apps.filter(a => a.status === 'Selected').length / apps.length) * 100).toFixed(1)
                        : '0.0',
                };
            });

            setDriveStats(stats);
            setAllApplications(allApps);
        } catch (err) {
            console.error('Error fetching report data', err);
            toast.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    // Totals
    const totalApps = allApplications.length;
    const totalSelected = allApplications.filter(a => a.status === 'Selected').length;
    const totalShortlisted = allApplications.filter(a => a.status === 'Shortlisted').length;
    const totalRejected = allApplications.filter(a => a.status === 'Rejected').length;

    // Branch-wise breakdown
    const branchStats = {};
    allApplications.forEach(app => {
        const branch = app.student?.branch || 'Unknown';
        if (!branchStats[branch]) branchStats[branch] = { total: 0, selected: 0 };
        branchStats[branch].total++;
        if (app.status === 'Selected') branchStats[branch].selected++;
    });
    const branchRows = Object.entries(branchStats).sort((a, b) => b[1].selected - a[1].selected);

    // Export all placed students
    const exportPlacedStudents = () => {
        const placed = allApplications.filter(a => a.status === 'Selected');
        if (placed.length === 0) { toast.error('No placed students to export'); return; }
        const headers = ['Enrollment No', 'Student Name', 'Branch', 'Semester', 'CGPA', 'Backlogs', 'Company / Drive', 'Resume Link'];
        const rows = placed.map(a => [
            a.student?.enrollmentNo || '',
            [a.student?.firstName, a.student?.middleName, a.student?.lastName].filter(Boolean).join(' ') || 'N/A',
            a.student?.branch || '',
            a.student?.semester || '',
            a.student?.cgpa ?? '',
            a.student?.activeBacklogs ?? '',
            `${a.companyName} - ${a.driveName}`,
            a.student?.resumeLink || '',
        ]);
        downloadCSV([headers, ...rows], `placed_students_${new Date().toISOString().slice(0, 10)}.csv`);
        toast.success(`Exported ${placed.length} placed students`);
    };

    // Export full applications report
    const exportFullReport = () => {
        if (allApplications.length === 0) { toast.error('No data to export'); return; }
        const headers = ['Enrollment No', 'Student Name', 'Branch', 'Semester', 'Batch', 'CGPA', 'Backlogs', 'Company', 'Drive', 'Status', 'Applied Date'];
        const rows = allApplications.map(a => [
            a.student?.enrollmentNo || '',
            [a.student?.firstName, a.student?.middleName, a.student?.lastName].filter(Boolean).join(' ') || 'N/A',
            a.student?.branch || '',
            a.student?.semester || '',
            a.student?.batch || '',
            a.student?.cgpa ?? '',
            a.student?.activeBacklogs ?? '',
            a.companyName,
            a.driveName,
            a.status,
            a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '',
        ]);
        downloadCSV([headers, ...rows], `placement_report_${new Date().toISOString().slice(0, 10)}.csv`);
        toast.success(`Exported ${allApplications.length} application records`);
    };

    const downloadCSV = (data, filename) => {
        const csv = data.map(row =>
            row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                {/* Header */}
                <div className="bento-header-banner flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <FiBarChart2 className="text-xl" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Placement Reports & Analytics</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                                Drive-wise statistics, branch breakdown, and CSV export of placement outcomes.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={fetchData}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition cursor-pointer">
                            <FiRefreshCw size={12} /> Refresh
                        </button>
                        <button onClick={exportPlacedStudents}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer">
                            <FiDownload size={12} /> Export Placed Students
                        </button>
                        <button onClick={exportFullReport}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer">
                            <FiFileText size={12} /> Full Report CSV
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {loading ? (
                        <div className="text-center py-14 text-slate-400 text-xs font-semibold">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent mb-2"></div>
                            <p>Loading placement data...</p>
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Applications</p>
                                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalApps}</p>
                                </div>
                                <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-800/60 rounded-xl">
                                    <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Students Selected</p>
                                    <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">{totalSelected}</p>
                                </div>
                                <div className="p-4 bg-amber-50/60 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-800/60 rounded-xl">
                                    <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Shortlisted</p>
                                    <p className="text-3xl font-bold text-amber-700 dark:text-amber-300 mt-1">{totalShortlisted}</p>
                                </div>
                                <div className="p-4 bg-rose-50/60 dark:bg-rose-950/40 border border-rose-100 dark:border-rose-800/60 rounded-xl">
                                    <p className="text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider">Rejected</p>
                                    <p className="text-3xl font-bold text-rose-700 dark:text-rose-300 mt-1">{totalRejected}</p>
                                </div>
                            </div>

                            {/* Drive-wise Table */}
                            <div>
                                <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                                    <FiBarChart2 className="text-indigo-600 dark:text-indigo-400" />
                                    Drive-wise Breakdown
                                </h2>
                                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                                        <thead className="bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                                            <tr>
                                                <th className="py-3 px-4 text-left">Company / Drive</th>
                                                <th className="py-3 px-4 text-left">Date</th>
                                                <th className="py-3 px-4 text-center">Total</th>
                                                <th className="py-3 px-4 text-center">Applied</th>
                                                <th className="py-3 px-4 text-center">Shortlisted</th>
                                                <th className="py-3 px-4 text-center">Selected</th>
                                                <th className="py-3 px-4 text-center">Rejected</th>
                                                <th className="py-3 px-4 text-center">Selection Rate</th>
                                                <th className="py-3 px-4 text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {driveStats.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="py-10 text-center text-slate-400 text-xs font-semibold">
                                                        No drives found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                driveStats.map(d => (
                                                    <tr key={d.driveId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-slate-800 dark:text-slate-200">
                                                        <td className="py-3 px-4">
                                                            <p className="font-bold text-slate-900 dark:text-white">{d.companyName}</p>
                                                            <p className="text-[10px] text-slate-400">{d.driveTitle}</p>
                                                        </td>
                                                        <td className="py-3 px-4 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                                            {d.driveDate ? new Date(d.driveDate).toLocaleDateString() : '—'}
                                                        </td>
                                                        <td className="py-3 px-4 text-center font-bold">{d.total}</td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 rounded font-bold border border-blue-200 dark:border-blue-800">{d.applied}</span>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className="px-2 py-0.5 bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 rounded font-bold border border-amber-200 dark:border-amber-800">{d.shortlisted}</span>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded font-bold border border-emerald-200 dark:border-emerald-800">{d.selected}</span>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className="px-2 py-0.5 bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 rounded font-bold border border-rose-200 dark:border-rose-800">{d.rejected}</span>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <div className="flex flex-col items-center gap-1">
                                                                <span className="font-bold text-indigo-600 dark:text-indigo-400">{d.selectionRate}%</span>
                                                                <div className="w-16 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(d.selectionRate, 100)}%` }} />
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${
                                                                d.status === 'Upcoming' ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                                                                d.status === 'Ongoing' ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800' :
                                                                d.status === 'Completed' ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' :
                                                                'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                                                            }`}>
                                                                {d.status || 'Upcoming'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Branch-wise Breakdown */}
                            {branchRows.length > 0 && (
                                <div>
                                    <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                                        <FiUsers className="text-indigo-600 dark:text-indigo-400" />
                                        Branch-wise Performance
                                    </h2>
                                    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                                            <thead className="bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                                                <tr>
                                                    <th className="py-3 px-4 text-left">Branch</th>
                                                    <th className="py-3 px-4 text-center">Applications</th>
                                                    <th className="py-3 px-4 text-center">Selected</th>
                                                    <th className="py-3 px-4 text-left">Selection Rate</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                {branchRows.map(([branch, data]) => {
                                                    const rate = data.total > 0 ? ((data.selected / data.total) * 100).toFixed(1) : '0.0';
                                                    return (
                                                        <tr key={branch} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-slate-800 dark:text-slate-200">
                                                            <td className="py-3 px-4 font-bold text-slate-900 dark:text-white">{branch}</td>
                                                            <td className="py-3 px-4 text-center font-semibold">{data.total}</td>
                                                            <td className="py-3 px-4 text-center">
                                                                <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded font-bold border border-emerald-200 dark:border-emerald-800">{data.selected}</span>
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden max-w-[120px]">
                                                                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(rate, 100)}%` }} />
                                                                    </div>
                                                                    <span className="font-bold text-indigo-600 dark:text-indigo-400">{rate}%</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {allApplications.length === 0 && (
                                <div className="text-center py-10 text-slate-400 text-xs font-semibold bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                                    No application data available yet. Add placement drives and students will start applying.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Reports;
